import ChartModal from '@/components/ChartModal';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { env } from '@/config/env';
import { supabase } from '@/lib/supabase';
import type { MusicData } from '@/types/music';
import ReactECharts from 'echarts-for-react';
import {
  ArrowLeft,
  BarChart2,
  Calendar,
  Clock,
  Home,
  Music,
  Music2,
  Play,
  X,
  Youtube,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface ArtistSong {
  title: string;
  count: number;
  firstAppearance: string;
  recentPlays: string[];
}

interface ArtistChartData {
  broadcast_date: string;
  count: number;
}

const ArtistPage: React.FC = () => {
  const { artistName } = useParams<{ artistName: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [songs, setSongs] = useState<ArtistSong[]>([]);
  const [totalPlays, setTotalPlays] = useState<number>(0);
  const [artistChartData, setArtistChartData] = useState<ArtistChartData[]>([]);
  const [artistImage, setArtistImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [imageError, setImageError] = useState<boolean>(false);
  const chartRef = useRef<ReactECharts>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedSong, setSelectedSong] = useState<{ title: string; artist: string } | null>(null);

  // Fetch artist data
  useEffect(() => {
    const fetchArtistData = async () => {
      if (!artistName) return;

      try {
        setLoading(true);
        setError(null);

        let allSongData: MusicData[] = [];
        let currentPage = 0;
        const pageSize = 1000; // Supabase default limit

        while (true) {
          const from = currentPage * pageSize;
          const to = from + pageSize - 1;

          // Fetch a chunk of songs by the artist
          const { data: songChunk, error: chunkError } = await supabase
            .from('music_data')
            .select('*')
            .ilike('artist', artistName)
            .order('broadcast_date', { ascending: false })
            .range(from, to); // Use range for pagination

          if (chunkError) {
            throw new Error(`데이터 조회 중 오류가 발생했습니다: ${chunkError.message}`);
          }

          if (songChunk) {
            allSongData = allSongData.concat(songChunk);
          }

          // Break loop if last page or no data
          if (!songChunk || songChunk.length < pageSize) {
            break;
          }

          currentPage++;
        }

        if (allSongData.length === 0) {
          throw new Error('해당 아티스트의 데이터가 없습니다.');
        }

        // Process song data using allSongData instead of songData
        const songMap = new Map<string, ArtistSong>();
        for (const item of allSongData) {
          const lowerCaseTitle = item.title.toLowerCase();
          if (!songMap.has(lowerCaseTitle)) {
            songMap.set(lowerCaseTitle, {
              title: item.title,
              count: 0,
              firstAppearance: item.broadcast_date,
              recentPlays: [],
            });
          }

          const song = songMap.get(lowerCaseTitle);
          if (song) {
            song.count++;

            // Update first appearance if this is earlier
            if (new Date(item.broadcast_date) < new Date(song.firstAppearance)) {
              song.firstAppearance = item.broadcast_date;
            }

            // Add broadcast date to recent plays if not already included
            if (!song.recentPlays.includes(item.broadcast_date)) {
              song.recentPlays.push(item.broadcast_date);
            }
          }
        }

        // Sort songs by play count (descending)
        const sortedSongs = Array.from(songMap.values()).sort((a, b) => b.count - a.count);

        // Calculate total plays
        const total = sortedSongs.reduce((sum, song) => sum + song.count, 0);

        setSongs(sortedSongs);
        setTotalPlays(total);

        // Aggregate play data by month for chart using allSongData
        const aggregatedData = allSongData.reduce(
          (acc: { [key: string]: number }, item: MusicData) => {
            if (!item.broadcast_date) return acc;
            const date = new Date(item.broadcast_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            acc[monthKey] = (acc[monthKey] || 0) + 1;
            return acc;
          },
          {}
        );

        // Convert aggregated data to chart format
        const chartDataArray = Object.entries(aggregatedData)
          .map(([date, count]) => ({
            broadcast_date: date,
            count: count as number,
          }))
          .sort((a, b) => a.broadcast_date.localeCompare(b.broadcast_date));

        setArtistChartData(chartDataArray);
      } catch (error) {
        console.error('Error fetching artist data:', error);
        setError(
          error instanceof Error ? error.message : '데이터를 불러오는 중 오류가 발생했습니다.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchArtistData();
  }, [artistName]);

  // Fetch artist image
  useEffect(() => {
    const fetchArtistImage = async () => {
      if (!artistName) return;

      setImageLoading(true);
      setImageError(false);
      setArtistImage(null);

      try {
        const apiKey = env.GOOGLE_API_KEY;
        const cseId = env.GOOGLE_CSE_ID;

        if (!apiKey || !cseId) {
          console.error('Google API credentials are missing');
          setImageError(true);
          setImageLoading(false);
          return;
        }

        // Artist search queries
        const artistSearchQueries = [
          `${artistName} official photo`,
          `${artistName} profile picture`,
          `${artistName} singer photo`,
          `${artistName} artist photo`,
          `${artistName} musician photo`,
          `${artistName} portrait`,
          `${artistName} photo`,
          `${artistName} picture`,
          `${artistName} singer`,
          `${artistName} artist`,
          `${artistName} musician`,
        ];

        let foundImage = null;

        // Try each search query
        for (const searchQuery of artistSearchQueries) {
          const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(searchQuery)}&cx=${cseId}&key=${apiKey}&searchType=image&imgSize=medium&imgType=photo&safe=active`;

          const response = await fetch(apiUrl, {
            headers: {
              Accept: 'application/json',
            },
          });

          if (!response.ok) continue;

          const data = await response.json();

          if (data.items && data.items.length > 0) {
            for (const img of data.items) {
              const title = img.title.toLowerCase();
              const artist_lower = artistName.toLowerCase();

              const isGoodSize = img.image?.width >= 150 && img.image?.height >= 150;
              const hasArtistName =
                title.includes(artist_lower) ||
                artist_lower.includes(title) ||
                title.split(' ').some((word: string) => artist_lower.includes(word)) ||
                artist_lower.split(' ').some((word: string) => title.includes(word));
              const isNotUnwanted =
                !title.includes('fan') &&
                !title.includes('concert') &&
                !title.includes('group') &&
                !title.includes('band') &&
                !title.includes('logo') &&
                !title.includes('icon') &&
                !title.includes('poster') &&
                !title.includes('banner');

              if (isGoodSize && hasArtistName && isNotUnwanted) {
                foundImage = img.link;
                break;
              }
            }

            if (foundImage) break;
          }
        }

        if (foundImage) {
          const validateImage = async (url: string) => {
            try {
              const response = await fetch(url, { method: 'HEAD' });
              return response.ok;
            } catch (error) {
              console.error('Image validation error:', error);
              return false;
            }
          };

          if (await validateImage(foundImage)) {
            const img = new Image();
            img.src = foundImage;
            img.onload = () => {
              setArtistImage(foundImage);
              setImageLoading(false);
            };
            img.onerror = () => {
              setImageError(true);
              setImageLoading(false);
            };
          } else {
            setImageError(true);
            setImageLoading(false);
          }
        } else {
          setImageError(true);
          setImageLoading(false);
        }
      } catch (error) {
        console.error('Error fetching artist image:', error);
        setImageError(true);
        setImageLoading(false);
      }
    };

    fetchArtistImage();
  }, [artistName]);

  // Chart options configuration
  const chartOption = {
    animation: true,
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      formatter: (params: unknown) => {
        const paramsArray = Array.isArray(params) ? params : [params];
        const firstParam = paramsArray[0] as { axisValue?: string; data?: number };
        const date = firstParam?.axisValue ?? '';
        const count = firstParam?.data ?? 0;
        return `${date}<br/>재생 횟수: ${count}회`;
      },
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: '#ddd',
      borderWidth: 1,
      textStyle: {
        color: '#333',
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '15%',
      top: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: artistChartData.map((item) => item.broadcast_date),
      axisLabel: {
        formatter: (value: string) => {
          const [year, month] = value.split('-');
          return `${year} ${month}`;
        },
        color: '#666',
        rotate: 45,
        margin: 15,
        interval: 'auto',
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
        },
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: {
        color: '#666',
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
        },
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0',
        },
      },
    },
    series: [
      {
        data: artistChartData.map((item) => item.count),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#10b981',
        },
        itemStyle: {
          color: '#10b981',
          borderColor: '#fff',
          borderWidth: 2,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(16, 185, 129, 0.3)',
              },
              {
                offset: 1,
                color: 'rgba(16, 185, 129, 0.1)',
              },
            ],
          },
        },
      },
    ],
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleSongClick = (songTitle: string) => {
    setSelectedSong({ title: songTitle, artist: artistName || '' });
    setIsModalOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Navigation buttons */}
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-gray-300 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleHome}
              className="text-gray-300 hover:text-white"
            >
              <Home className="mr-2 h-4 w-4" />
              Home
            </Button>
          </div>

          {/* Artist Header */}
          <div className="flex items-start gap-6 mb-8">
            {/* Artist Image */}
            <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0">
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800/80 rounded-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                </div>
              )}
              {!imageLoading && artistImage && (
                <img
                  src={artistImage}
                  alt={artistName}
                  className="w-full h-full rounded-full object-cover shadow-md aspect-square"
                />
              )}
              {!imageLoading && !artistImage && (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center shadow-inner aspect-square">
                  <span className="text-gray-300 text-4xl font-bold">
                    {artistName?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Artist Info */}
            <div className="flex flex-col flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{artistName}</h1>

              {loading ? (
                <div className="animate-pulse h-6 bg-gray-800 rounded w-32 mb-2" />
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  <Music className="h-5 w-5 text-primary" />
                  <span className="text-xl font-semibold text-white">{songs.length} songs</span>
                  <span className="text-gray-300">•</span>
                  <Play className="h-4 w-4 text-primary" />
                  <span className="text-xl font-semibold text-white">{totalPlays} plays</span>
                </div>
              )}

              {loading ? (
                <div className="animate-pulse h-4 bg-gray-800 rounded w-48 mb-1" />
              ) : (
                songs.length > 0 && (
                  <div className="text-gray-300 flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>
                      First appearance:{' '}
                      {songs.reduce(
                        (earliest, song) =>
                          new Date(song.firstAppearance) < new Date(earliest)
                            ? song.firstAppearance
                            : earliest,
                        songs[0].firstAppearance
                      )}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Play Trend Chart */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-8 shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-white">Play Trends</h2>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
              </div>
            ) : error ? (
              <div className="h-[300px] flex items-center justify-center text-red-400">{error}</div>
            ) : artistChartData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-gray-300">
                No play records available
              </div>
            ) : (
              <div className="h-[300px]">
                <ReactECharts
                  ref={chartRef}
                  option={chartOption}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                  notMerge={true}
                  lazyUpdate={true}
                  theme="dark"
                />
              </div>
            )}
          </div>

          {/* Song List */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-white">Songs</h2>
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }, () => (
                  <div
                    key={`skeleton-${Math.random().toString(36).substring(2, 9)}`}
                    className="animate-pulse flex flex-col p-3 border border-gray-800/50 rounded-lg"
                  >
                    <div className="h-5 bg-gray-800 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-800 rounded w-1/4 opacity-70" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-400">{error}</div>
            ) : songs.length === 0 ? (
              <div className="p-4 text-center text-gray-300">No songs found for this artist</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {songs.map((song) => (
                  <div
                    key={song.title}
                    className="flex flex-col p-4 border border-gray-800/50 rounded-lg hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex justify-between items-center mb-2">
                      {/* Song Title */}
                      <h3 className="text-lg font-medium text-white truncate flex-1 mr-2">
                        {song.title}
                      </h3>
                      {/* Play Count Badge */}
                      <div className="flex items-center px-2 py-1 bg-gray-800/70 rounded-full flex-shrink-0">
                        <Play className="h-3 w-3 mr-1 text-primary" />
                        <span className="text-xs font-medium text-white">{song.count}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mb-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `https://www.youtube.com/results?search_query=${encodeURIComponent(
                                  `${artistName} ${song.title}`
                                )}`,
                                '_blank'
                              );
                            }}
                            className="text-muted-foreground hover:text-primary flex-shrink-0 h-6 w-6 p-0"
                          >
                            <Youtube className="h-4 w-4 text-[#FF0000]" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Search on YouTube</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSongClick(song.title)}
                            className="text-muted-foreground hover:text-primary flex-shrink-0 h-6 w-6 p-0"
                          >
                            <BarChart2 className="h-4 w-4 text-primary" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>View Chart</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Song Metadata */}
                    <div className="text-xs text-gray-400">
                      <div className="mb-1">First appeared: {song.firstAppearance}</div>
                      {song.recentPlays.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span>
                            Recent plays: {song.recentPlays.slice(0, 3).join(', ')}
                            {song.recentPlays.length > 3 &&
                              ` (${song.recentPlays.length - 3} more)`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chart Modal */}
        {selectedSong && (
          <ChartModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={selectedSong.title}
            artist={selectedSong.artist}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default ArtistPage;
